import { Key } from "@multiweb/reactUtils";

/**
 * Vrací výchozí podmínku pro načítání dat ze serveru.
 *
 * @param recordIdKey Klíč k identifikátoru uvnitř načítané třídy.
 */
export const getDefaultCondition = (recordIdKey: string) => `${recordIdKey} <> 0`;

/**
 * Pole hodnot pro identifikaci událostí stisknutí kláves, které jsou implementovány
 * pro výběr více záznamů v modulu DataSource.
 */
export const multiselectControlKeys: ReadonlyArray<string> = [Key.ArrowDown, Key.ArrowUp];

/**
 * Jméno modulu DataSource. Slouží pro jednoznačnou identifikaci modulu.
 */
export const NAME = "dataSource";

export const createFetchAttributesLocalizationsKey = (dataSourceKey: string) =>
    `${NAME}/${dataSourceKey}/FETCH_ATTRIBUTES_LOCALIZATIONS`;
export const createFetchActionsLocalizationsKey = (dataSourceKey: string) =>
    `${NAME}/${dataSourceKey}/FETCH_ACTIONS_LOCALIZATIONS`;

/**
 * Konstanty typů akcí.
 */
const createActionType = <T extends string>(actionName: T) => {
    return `${NAME}/${actionName}` as const;
};

/**
 * Typ akce pro inicializaci DataSource.
 */
export const INIT_DATASOURCE = createActionType("INIT_DATASOURCE");

/**
 * Typ akce pro smazání DataSource.
 */
export const DELETE_DATASOURCE = createActionType("DELETE_DATASOURCE");

/**
 * Typ akce pro načtení informací k atributům.
 */
export const LOAD_ATTRIBUTE_INFO = createActionType("LOAD_ATTRIBUTE_INFO");

/**
 * Typ akce pro úspěšné načtení informací k atributům.
 */
export const LOAD_ATTRIBUTE_INFO_SUCCESS = createActionType("LOAD_ATTRIBUTE_INFO_SUCCESS");

/**
 * Typ akce pro neúspěšné načtení informací k atributům.
 */
export const LOAD_ATTRIBUTE_INFO_ERROR = createActionType("LOAD_ATTRIBUTE_INFO_ERROR");

/**
 * Typ akce pro načtení nových dat do DataSource.
 */
export const LOAD_DATA = createActionType("LOAD_DATA");

/**
 * Typ akce pro úspěšné načtení dat.
 */
export const LOAD_DATA_SUCCESS = createActionType("LOAD_DATA_SUCCESS");

/**
 * Typ akce pro neúspěšné načtení dat.
 */
export const LOAD_DATA_ERROR = createActionType("LOAD_DATA_ERROR");

/**
 * Typ akce pro aktualizaci dat v DataSource.
 */
export const REFRESH_DATA = createActionType("REFRESH_DATA");

/**
 * Typ akce pro uspěšnou aktualizaci dat v DataSource.
 */
export const REFRESH_DATA_SUCCESS = createActionType("REFRESH_DATA_SUCCESS");

/**
 * Typ akce pro neúspěšnou aktualizaci dat v DataSource.
 */
export const REFRESH_DATA_ERROR = createActionType("REFRESH_DATA_ERROR");

/**
 * Typ akce pro aktualizaci fultextové podmínky DataSource.
 */
export const UPDATE_FULLTEXT_CONDITION = createActionType("UPDATE_FULLTEXT_CONDITION");

/**
 * Typ akce pro aktualizaci masterFrame podmínky DataSource.
 */
export const UPDATE_MASTER_FRAME_CONDITION = createActionType("UPDATE_MASTER_FRAME_CONDITION");

/**
 * Typ akce pro aktualizaci akcí pro záznam z MW.
 */
export const UPDATE_RECORD_ACTIONS = createActionType("UPDATE_RECORD_ACTIONS");

/**
 * Typ akce pro uložení aktualizovaných akcí pro záznam z MW.
 */
export const UPDATE_RECORD_ACTIONS_SUCCESS = createActionType("UPDATE_RECORD_ACTIONS_SUCCESS");

/**
 * Typ akce pro nastavení řazení.
 */
export const SET_ORDER_BY = createActionType("SET_ORDER_BY");

/**
 * Typ akce pro přepnutí řazení.
 */
export const TOGGLE_ORDER_BY = createActionType("TOGGLE_ORDER_BY");

/**
 * Typ akce pro nastavení režimu výběru více záznamů na datasource.
 */
export const SET_DATASOURCE_MULTISELECT_MODE = createActionType("SET_DATASOURCE_MULTISELECT_MODE");

/**
 * Typ akce pro nastavení atributů.
 */
export const SET_ATTRIBUTES = createActionType("SET_ATTRIBUTES");

/**
 * Typ akce pro výběr všech načtených a přednačtených záznamů v DataSource.
 */
export const CHECK_ALL_RECORDS = createActionType("CHECK_ALL_RECORDS");

/**
 * Typ akce pro zaškrtnutí či odškrtnutí všech záznamů z Datasource.
 */
export const TOGGLE_CHECK_ALL_RECORDS = createActionType("TOGGLE_CHECK_ALL_RECORDS");

/**
 * Typ akce pro přepnutí označení(isChecked) na vybraných(isSelected) záznamech v DataSource.
 */
export const TOGGLE_CHECK_ON_SELECTED_RECORDS = createActionType("TOGGLE_CHECK_ON_SELECTED_RECORDS");

/**
 * Typ akce pro označení / odznačení konkrétního záznamu v DataSource.
 */
export const TOGGLE_SELECTED_RECORD_CHECK = createActionType("TOGGLE_SELECTED_RECORD_CHECK");

/**
 * Typ akce pro vyběr dalšího záznamu.
 */
export const SELECT_NEXT_RECORD = createActionType("SELECT_NEXT_RECORD");

/**
 * Typ akce pro vyběr předchozího záznamu.
 */
export const SELECT_PREVIOUS_RECORD = createActionType("SELECT_PREVIOUS_RECORD");

/**
 * Typ akce pro nastavení informace o tom, zda je na DataSource zakázáno načítání dat.
 */
export const SET_DISABLE_LOADING = createActionType("SET_DISABLE_LOADING");

/**
 * Typ akce pro nastavení informace o tom, zda se na DataSource může otevřít formulář zobrazující detail záznamu.
 */
export const SET_HAS_DETAIL_FORM = createActionType("SET_HAS_DETAIL_FORM");

/**
 * Typ akce pro zrušení výberů všech záznamů.
 */
export const DESELECT_ALL_RECORDS = createActionType("DESELECT_ALL_RECORDS");

/**
 * Typ akce pro výběr záznamů daných identifikátory.
 */
export const SELECT_RECORDS = createActionType("SELECT_RECORDS");

/**
 * Typ akce pro výběr jednoho záznamu
 */
export const SELECT_RECORD = createActionType("SELECT_RECORD");

/**
 * Typ akce pro odznačení všech záznamů.
 */
export const UNCHECK_ALL_RECORDS = createActionType("UNCHECK_ALL_RECORDS");

/**
 * Typ akce pro nastavení identifikátoru naposledy označeného záznamu.
 */
export const SET_LAST_SELECTED_RECORD_ID = createActionType("SET_LAST_SELECTED_RECORD_ID");

/**
 * Typ akce pro získání workflow akcí.
 */
export const GET_WORKFLOW_ACTIONS_REQUEST = createActionType("GET_WORKFLOW_ACTIONS_REQUEST");

/**
 * Typ akce pro uložení workflow akcí do storu.
 */
export const GET_WORKFLOW_ACTIONS_SUCCESS = createActionType("GET_WORKFLOW_ACTIONS_SUCCESS");

/**
 * Typ akce pro neúspěšné získání workflow akcí.
 */
export const GET_WORKFLOW_ACTIONS_ERROR = createActionType("GET_WORKFLOW_ACTION_ERROR");

/**
 * Typ akce pro provedení workflow akce.
 */
export const EXECUTE_WORKFLOW_ACTION = createActionType("EXECUTE_WORKFLOW_ACTION");

/**
 * Typ akce pro nastavení uzlových záznamů.
 */
export const SET_ROOT_NODES = createActionType("SET_ROOT_NODES");
